// Used with the Scheduler to visualize a timeline for each field

import { useRef } from 'react';
import { useDrop } from 'react-dnd';
import { toast } from 'react-toastify';
import moment from 'moment';
import GameBlock, { ItemTypes } from './GameBlock';
import { parseTimeToMinutes, time24To12H } from '../utils/misc';
import { gameDivision } from '../utils/gameReport';
import {
  scheduleStartHour,
  scheduleStepMin,
  timeSlotHeight,
} from '../utils/consts';
import { useFirebase } from './FirebaseContextProvider';
import './Field.scss';

const timeSlotsInHour = Math.floor(60 / scheduleStepMin);

function minutesFromTime24(t) {
  return parseTimeToMinutes(time24To12H(t));
}

export function intervalsOverlap(start1, dur1, start2, dur2) {
  const s1 = minutesFromTime24(start1);
  const e1 = s1 + dur1;
  const s2 = minutesFromTime24(start2);
  const e2 = s2 + dur2;
  return s2 < e1 && s1 < e2;
}

function getOverlappedGames({ games, excludeId, field, start, duration }) {
  return games
    .filter((g) => g.id !== excludeId)
    .filter((g) => g.field === field)
    .filter((g) => intervalsOverlap(start, duration, g.start, g.duration));
}

function applyGameUpdates(games, updatesById) {
  return games.map((g) =>
    updatesById[g.id] ? { ...g, ...updatesById[g.id] } : g,
  );
}

function toastOverlapFlags(prefix, { slot, team, coach, referee }) {
  if (slot) toast.error(`${prefix}Slot is busy`);
  if (team) toast.error(`${prefix}Team overlap`);
  if (coach) toast.error(`${prefix}Coach overlap`);
  if (referee) toast.error(`${prefix}Referee overlap`);
}

// Choose the game under cursor (for swap priority)
function getGameUnderCursor({ games, field, excludeId, rectTop, cursorY }) {
  const cursorMinutes =
    scheduleStartHour * 60 +
    ((cursorY - rectTop) / timeSlotHeight) * scheduleStepMin;

  return games
    .filter((g) => g.field === field)
    .filter((g) => g.id !== excludeId)
    .find((g) => {
      const s = minutesFromTime24(g.start);
      const e = s + g.duration;
      return cursorMinutes >= s && cursorMinutes < e;
    });
}

export function overlapCheck({
  games,
  gameId,
  field,
  start,
  duration,
  coachesByTeamId,
  referees = null,
  newTeams = null,
}) {
  const game = games.find((g) => g.id === gameId);
  const otherGames = games.filter((g) => g.id !== gameId);
  const sameField = otherGames.filter((g) => g.field === field);
  const diffField = otherGames.filter((g) => g.field !== field);

  let teamsToCheck = newTeams;
  if (!teamsToCheck) teamsToCheck = game ? [game.team1, game.team2] : [];
  teamsToCheck = teamsToCheck.filter((t) => t != null);

  const refsToCheck = Array.isArray(referees)
    ? referees
    : Array.isArray(game?.referees)
      ? game.referees
      : [];

  const slot = sameField.some((g) =>
    intervalsOverlap(start, duration, g.start, g.duration),
  );

  const team = diffField.some((g) => {
    if (!teamsToCheck.length) return false;
    const shared = teamsToCheck.some(
      (tId) =>
        (g.team1 != null && g.team1 === tId) ||
        (g.team2 != null && g.team2 === tId),
    );
    return shared && intervalsOverlap(start, duration, g.start, g.duration);
  });

  const getCoachIds = (t) => (coachesByTeamId.get(t) || []).map((c) => c.id);
  const gameCoaches = teamsToCheck.flatMap(getCoachIds);

  const coach = diffField.some((g) => {
    const gCoaches = [g.team1, g.team2].flatMap(getCoachIds);
    const shared = gCoaches.some((c) => gameCoaches.includes(c));
    return shared && intervalsOverlap(start, duration, g.start, g.duration);
  });

  const referee = diffField.some((g) => {
    if (!g.referees || !g.referees.length) return false;
    const shared = g.referees.some((r) => refsToCheck.includes(r));
    return shared && intervalsOverlap(start, duration, g.start, g.duration);
  });

  return { slot, team, coach, referee };
}

export default function Field({
  id,
  field,
  timeSlots,
  games,
  colorLegend,
  setBracketsPopup,
  setGame,
  highlightedGameId,
  onSearchTermClick,
  onSearchHoverChange,
  activeSearchHoverKey,
}) {
  const { userIsAdmin, teamsById, coachesByTeamId } = useFirebase();
  const bodyRef = useRef();

  const [, dropRef] = useDrop({
    accept: userIsAdmin ? [ItemTypes.GAME_BLOCK, ItemTypes.RESIZE_HANDLE] : [],
    canDrop: () => userIsAdmin,
    drop: async (item, monitor) => {
      if (!userIsAdmin) return;
      const dropTarget = bodyRef.current;
      if (!dropTarget) return;

      const sourceOffset = monitor.getSourceClientOffset();
      if (!sourceOffset) return;

      const rect = dropTarget.getBoundingClientRect();
      const offsetY = sourceOffset.y - rect.top;
      const slotIndex = Math.max(
        0,
        Math.min(
          timeSlots.length - 1,
          Math.floor((offsetY + timeSlotHeight / 2) / timeSlotHeight),
        ),
      );

      const totalMinutes = scheduleStartHour * 60 + slotIndex * scheduleStepMin;
      const hour = Math.floor(totalMinutes / 60)
        .toString()
        .padStart(2, '0');
      const min = (totalMinutes % 60).toString().padStart(2, '0');
      let start = `${hour}:${min}`;

      const draggedGame = games.find((g) => g.id === item.id);
      if (!draggedGame) return;

      const diffMin = moment(draggedGame.start, 'HH:mm').diff(
        moment(start, 'HH:mm'),
        'minutes',
      );

      let duration = draggedGame.duration;

      // Resize path: keep old behavior (no swapping)
      if (item.resizeType) {
        if (item.resizeType === 'start') {
          duration = draggedGame.duration + diffMin;
        } else if (item.resizeType === 'end') {
          duration = -1 * diffMin;
          start = draggedGame.start;
        }
        if (duration < scheduleStepMin) duration = scheduleStepMin;

        const overlaps = overlapCheck({
          field,
          start,
          duration,
          games,
          coachesByTeamId,
          gameId: item.id,
        });

        toastOverlapFlags('', overlaps);
        if (
          overlaps.slot ||
          overlaps.team ||
          overlaps.coach ||
          overlaps.referee
        )
          return;

        try {
          await setGame({
            id,
            gameId: item.id,
            field,
            start,
            duration,
          });
        } catch (error) {
          toast.error(error?.message ?? 'Failed to update game');
        }
        return;
      }

      const itemType = monitor.getItemType?.();

      // Determine "swap target" by cursor first
      let cursorTarget = null;
      const clientOffset = monitor.getClientOffset();
      if (clientOffset) {
        cursorTarget = getGameUnderCursor({
          games,
          field,
          excludeId: draggedGame.id,
          rectTop: rect.top,
          cursorY: clientOffset.y,
        });
      }

      const overlaps = overlapCheck({
        field,
        start,
        duration,
        games,
        coachesByTeamId,
        gameId: item.id,
      });

      // Swap logic (only for GAME_BLOCK)
      if (itemType === ItemTypes.GAME_BLOCK && overlaps.slot) {
        // Fallback to "interval overlapped games" only if no cursor target
        const overlappedGames = cursorTarget
          ? []
          : getOverlappedGames({
              games,
              excludeId: draggedGame.id,
              field,
              start,
              duration,
            });

        const targetGame =
          cursorTarget ||
          (overlappedGames.length === 1 ? overlappedGames[0] : null);

        if (!targetGame) {
          // Ambiguous slot: keep old behavior (show errors)
          toastOverlapFlags('', overlaps);
          return;
        }

        const aPrev = {
          field: draggedGame.field,
          start: draggedGame.start,
          duration: draggedGame.duration,
        };
        const bPrev = {
          field: targetGame.field,
          start: targetGame.start,
          duration: targetGame.duration,
        };

        // Each game fully takes the other game's slot
        const aNext = { ...bPrev };
        const bNext = { ...aPrev };

        const projectedGames = applyGameUpdates(games, {
          [draggedGame.id]: aNext,
          [targetGame.id]: bNext,
        });

        const overlapsA = overlapCheck({
          games: projectedGames,
          gameId: draggedGame.id,
          field: aNext.field,
          start: aNext.start,
          duration: aNext.duration,
          coachesByTeamId,
          referees: draggedGame.referees,
          newTeams: [draggedGame.team1, draggedGame.team2].filter(Boolean),
        });

        const overlapsB = overlapCheck({
          games: projectedGames,
          gameId: targetGame.id,
          field: bNext.field,
          start: bNext.start,
          duration: bNext.duration,
          coachesByTeamId,
          referees: targetGame.referees,
          newTeams: [targetGame.team1, targetGame.team2].filter(Boolean),
        });

        const swapBlocked = {
          slot: overlapsA.slot || overlapsB.slot,
          team: overlapsA.team || overlapsB.team,
          coach: overlapsA.coach || overlapsB.coach,
          referee: overlapsA.referee || overlapsB.referee,
        };

        if (
          swapBlocked.slot ||
          swapBlocked.team ||
          swapBlocked.coach ||
          swapBlocked.referee
        ) {
          toastOverlapFlags('Swap blocked: ', swapBlocked);
          return;
        }

        try {
          await Promise.all([
            setGame({
              id,
              gameId: draggedGame.id,
              field: aNext.field,
              start: aNext.start,
              duration: aNext.duration,
            }),
            setGame({
              id,
              gameId: targetGame.id,
              field: bNext.field,
              start: bNext.start,
              duration: bNext.duration,
            }),
          ]);
        } catch (error) {
          toast.error(error?.message ?? 'Failed to swap games');
        }

        return;
      }

      // Normal move path
      toastOverlapFlags('', overlaps);
      if (overlaps.slot || overlaps.team || overlaps.coach || overlaps.referee)
        return;

      try {
        await setGame({
          id,
          gameId: item.id,
          field,
          start,
          duration,
        });
      } catch (error) {
        toast.error(error?.message ?? 'Failed to move game');
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
  });

  return (
    <div className='field'>
      <div
        className='body'
        style={{ height: timeSlotHeight * timeSlots.length }}
        ref={bodyRef}
      >
        {timeSlots
          .filter((timeSlot, i) => i % timeSlotsInHour === 3)
          .map((timeSlot, i) => {
            if (i === 0) return null;
            return (
              <div
                key={timeSlot}
                className='divider'
                style={{ top: timeSlotsInHour * timeSlotHeight * i }}
              />
            );
          })}
        <div className='drop-zone' ref={dropRef}>
          {games
            .filter((g) => g.field === field)
            .map((game) => (
              <GameBlock
                key={game.id}
                id={id}
                game={game}
                highlighted={game.id === highlightedGameId}
                color={colorLegend[gameDivision(game, teamsById)]?.color}
                setBracketsPopup={setBracketsPopup}
                onSearchTermClick={onSearchTermClick}
                onSearchHoverChange={onSearchHoverChange}
                activeSearchHoverKey={activeSearchHoverKey}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
